/**
 * logger.ts
 * Production-ready logger utility for lt-notes portal.
 *
 * Features:
 * - Log levels: debug, info, warn, error
 * - Environment-driven level threshold (VITE_LOG_LEVEL env var, or auto-detect from DEV/PROD mode)
 * - ISO timestamps on each line
 * - No external dependencies (native console.*)
 * - Fully typed, strict mode compliant
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

interface LoggerConfig {
    level?: LogLevel;
    enableTimestamp?: boolean;
}

interface Logger {
    debug: (...args: any[]) => void;
    info: (...args: any[]) => void;
    warn: (...args: any[]) => void;
    error: (...args: any[]) => void;
}

const LOG_LEVEL_ORDER: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silent: 4,
};

/**
 * Get the effective log level from environment or defaults based on DEV/PROD mode.
 * Priority:
 * 1. VITE_LOG_LEVEL environment variable (if set to valid level)
 * 2. 'debug' if import.meta.env.DEV is true (development builds are verbose)
 * 3. 'warn' if import.meta.env.PROD is true (production builds are quiet, only warnings/errors)
 * 4. Fallback to 'info' if neither DEV nor PROD is detected
 */
function getEffectiveLogLevel(): LogLevel {
    // Check VITE_LOG_LEVEL env var first
    const envLevel = import.meta.env.VITE_LOG_LEVEL as string | undefined;
    if (envLevel && isValidLogLevel(envLevel)) {
        return envLevel as LogLevel;
    }

    // Auto-detect from build mode
    if (import.meta.env.DEV) {
        return 'debug';
    }
    if (import.meta.env.PROD) {
        return 'warn';
    }

    // Safe fallback
    return 'info';
}

function isValidLogLevel(level: string): boolean {
    return Object.prototype.hasOwnProperty.call(LOG_LEVEL_ORDER, level);
}

/**
 * Factory function to create a logger instance bound to a tag/source name.
 * Example:
 *   const logger = createLogger('HttpService');
 *   logger.debug('Initializing client...');  // logs "[HttpService] Initializing client..."
 *   logger.error('Connection failed', error);
 */
export function createLogger(tag: string, config?: LoggerConfig): Logger {
    const threshold = config?.level || getEffectiveLogLevel();
    const thresholdValue = LOG_LEVEL_ORDER[threshold];
    const enableTimestamp = config?.enableTimestamp ?? true;

    const formatMessage = (level: LogLevel, ...args: any[]): void => {
        const levelValue = LOG_LEVEL_ORDER[level];

        // Check if this level should be logged (threshold filtering)
        if (levelValue < thresholdValue) {
            return;
        }

        // Build prefix: timestamp (optional) + tag + level
        const timestamp = enableTimestamp ? `${new Date().toISOString()} ` : '';
        const prefix = `${timestamp}[${tag}:${level.toUpperCase()}]`;

        // Use appropriate console method
        // eslint-disable-next-line no-console
        if (level === 'error') {
            console.error(prefix, ...args);
        } else if (level === 'warn') {
            console.warn(prefix, ...args);
        } else {
            console.log(prefix, ...args);
        }
    };

    return {
        debug: (...args: any[]) => formatMessage('debug', ...args),
        info: (...args: any[]) => formatMessage('info', ...args),
        warn: (...args: any[]) => formatMessage('warn', ...args),
        error: (...args: any[]) => formatMessage('error', ...args),
    };
}

/**
 * Global singleton logger instance for app-wide use (e.g., initialization messages).
 */
export const globalLogger = createLogger('APP');
